// Account-backed progress. Everything a child does — practice/exam results, streaks, XP, Glow Up, study plans, and
// the progress every lesson page keeps — is stored in this browser's localStorage under keys starting "lumio" (the
// lessons are on the same origin, so they share it). Without this, a new device or a cleared browser starts from zero.
//
// While a parent is signed in, those keys are mirrored to one row per account in Supabase (public.account_progress):
//   pull on sign-in → any key changed more recently elsewhere is written here;
//   push every 20 s and when the tab is hidden → keys that changed here are uploaded.
// Each key carries the time it last changed on the device that changed it; the newer change wins, per key.
// Sign-in, plan, admin and other device-only keys are never synced (DENY below).

import { supabase, supabaseEnabled } from './supabase';

const KEY_RE = /^lumio[._-]/;
const DENY = new Set([
  'lumio_access', 'lumio_plan', 'lumio_used_emails', 'lumio_pending_signup', 'lumio_pilots', 'lumio_session',
  'lumio_exam_session', 'lumio_view', 'lumio_home_mode', 'lumio_device_id', 'lumio_trial_usage', 'lumio_reports',
  'lumio_lumi_observer_events', 'lumio_selected_curriculum', 'lumio_active_profile', // who is practising is chosen per device
]);
const DENY_PREFIX = ['lumio_sync_', 'lumio_admin'];
const META = 'lumio_sync_meta';
const MAX_VALUE = 400 * 1024;      // one key's value; anything bigger stays on this device
const PUSH_EVERY = 20000;

export function syncable(key) {
  return KEY_RE.test(key) && !DENY.has(key) && !DENY_PREFIX.some((p) => key.startsWith(p));
}

function hash(s) {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36) + ':' + s.length;
}

const readMeta = () => { try { return JSON.parse(localStorage.getItem(META) || '{}') || {}; } catch (_) { return {}; } };
const writeMeta = (m) => { try { localStorage.setItem(META, JSON.stringify(m)); } catch (_) {} };

/** Record which syncable keys changed (or disappeared) since the last scan. Returns the updated meta. */
export function scan(now = Date.now()) {
  const meta = readMeta();
  const seen = new Set();
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (!k || !syncable(k)) continue;
    const v = localStorage.getItem(k);
    if (v == null || v.length > MAX_VALUE) continue;
    seen.add(k);
    const h = hash(v);
    if (!meta[k] || meta[k].h !== h) meta[k] = { h, t: now };
  }
  for (const k of Object.keys(meta)) if (!seen.has(k) && meta[k].h !== null) meta[k] = { h: null, t: now }; // deleted here
  writeMeta(meta);
  return meta;
}

/** The record uploaded for this device: every tracked key with its value (null = deleted) and change time. */
export function snapshot(meta = scan()) {
  const data = {};
  for (const [k, m] of Object.entries(meta)) {
    const v = m.h === null ? null : localStorage.getItem(k);
    if (m.h !== null && v == null) continue;
    data[k] = { v, t: m.t };
  }
  return data;
}

/** Apply a remote record: each key whose remote change is newer than ours is written (or removed). Returns keys applied. */
export function applyRemote(remote) {
  const meta = scan();
  const applied = [];
  for (const [k, r] of Object.entries(remote || {})) {
    if (!syncable(k) || !r || typeof r.t !== 'number') continue;
    if ((meta[k]?.t || 0) >= r.t) continue;
    try {
      if (r.v === null) localStorage.removeItem(k);
      else if (typeof r.v === 'string' && r.v.length <= MAX_VALUE) localStorage.setItem(k, r.v);
      else continue;
    } catch (_) { continue; }
    meta[k] = { h: r.v === null ? null : hash(r.v), t: r.t };
    applied.push(k);
  }
  writeMeta(meta);
  return applied;
}

/** Merge for the upload: keep the newer of ours and what the server already has, per key (another device may be ahead). */
export function mergeForUpload(local, remote) {
  const out = { ...(remote || {}) };
  for (const [k, r] of Object.entries(local)) if (!out[k] || out[k].t < r.t) out[k] = r;
  return out;
}

async function fetchRemote(userId) {
  const { data, error } = await supabase.from('account_progress').select('data').eq('user_id', userId).maybeSingle();
  if (error) throw error;
  return (data && data.data) || {};
}

let lastPushed = '';
async function push(userId) {
  const local = snapshot(scan());
  const sig = hash(JSON.stringify(local));
  if (sig === lastPushed) return false;
  const remote = await fetchRemote(userId);
  const merged = mergeForUpload(local, remote);
  const { error } = await supabase.from('account_progress')
    .upsert({ user_id: userId, data: merged, updated_at: new Date().toISOString() }, { onConflict: 'user_id' });
  if (error) throw error;
  lastPushed = sig;
  return true;
}

/**
 * Start syncing for a signed-in user. onApplied(keys) runs after a pull wrote anything, so the app can re-read state.
 * Returns a stop() function. Silently does nothing when Supabase is not configured or the table is missing.
 */
export function startAccountSync(userId, onApplied) {
  if (!supabaseEnabled || !userId) return () => {};
  let stopped = false, timer = null;
  lastPushed = '';
  // First sync on this device: its existing progress has no change times, so stamp it as old (t=1). Anything the
  // account already holds then wins, and anything only this device has is still uploaded.
  if (localStorage.getItem(META) == null) scan(1);
  const safePush = () => { if (!stopped) push(userId).catch(() => {}); };
  const onHide = () => { if (document.visibilityState === 'hidden') safePush(); };
  (async () => {
    try {
      const applied = applyRemote(await fetchRemote(userId));
      if (applied.length && !stopped && onApplied) onApplied(applied);
    } catch (_) { /* offline or table missing: keep working locally */ }
    if (stopped) return;
    safePush();
    timer = setInterval(safePush, PUSH_EVERY);
    document.addEventListener('visibilitychange', onHide);
  })();
  return () => { stopped = true; if (timer) clearInterval(timer); document.removeEventListener('visibilitychange', onHide); };
}
